import React, { useEffect } from 'react';
import { Grid, Typography, Box, CircularProgress, useTheme, Card, CardContent } from '@mui/material';
import { TrendingUp, TrendingDown, BarChart, RemoveShoppingCart } from '@mui/icons-material';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';
import { Moment } from 'moment';

interface ProductActivitySummaryProps {
    dateRange?: [Moment, Moment];
}

const ProductActivitySummary: React.FC<ProductActivitySummaryProps> = ({ dateRange }) => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<{
        mostViewedProductId: string | null;
        mostViewedProductName: string | null;
        mostViewedCount: number;
        leastViewedProductId: string | null;
        leastViewedProductName: string | null;
        leastViewedCount: number;
        avgViews: number;
        zeroViewProducts: number;
    } | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const startDate = dateRange?.[0]?.toISOString();
                const endDate = dateRange?.[1]?.toISOString();
                const result = await analyticsService.getProductActivitySummary(startDate, endDate);
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch product activity summary');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch product activity summary');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    const cards = [
        {
            title: 'Most Viewed',
            value: data?.mostViewedCount || 0,
            subtitle: data?.mostViewedProductName || 'N/A',
            icon: <TrendingUp sx={{ fontSize: 20, color: theme.palette.success.main }} />,
            color: theme.palette.success.main
        },
        {
            title: 'Least Viewed',
            value: data?.leastViewedCount || 0,
            subtitle: data?.leastViewedProductName || 'N/A',
            icon: <TrendingDown sx={{ fontSize: 20, color: theme.palette.warning.main }} />,
            color: theme.palette.warning.main
        },
        {
            title: 'Avg Views',
            value: data?.avgViews?.toFixed(1) || '0.0',
            subtitle: 'Per product',
            icon: <BarChart sx={{ fontSize: 20, color: theme.palette.info.main }} />,
            color: theme.palette.info.main
        },
        {
            title: 'Zero Views',
            value: data?.zeroViewProducts || 0,
            subtitle: 'Products',
            icon: <RemoveShoppingCart sx={{ fontSize: 20, color: theme.palette.error.main }} />,
            color: theme.palette.error.main
        }
    ];

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Product Activity Summary
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Key product engagement metrics
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                    <Typography variant='body2' color='error' fontFamily='primaryFont'>
                        {error}
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {cards.map((card, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    border: `1px solid ${theme.palette.divider}`,
                                    '&:hover': {
                                        boxShadow: theme.shadows[4],
                                    }
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                        {card.icon}
                                        <Typography variant='subtitle2' fontFamily='primaryFont' fontWeight={600} color='text.secondary'>
                                            {card.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant='h4' fontFamily='primaryFont' fontWeight='bold' color={card.color} sx={{ mb: 0.5 }}>
                                        {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                        {card.subtitle}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </PortalCard>
    );
};

export default ProductActivitySummary;

